/**
 * PublicShell — marketing + auth + /p/:slug layout. Sticky blurred
 * top-nav, full-bleed body, persistent footer. The body renders via
 * <Outlet /> so each public route stays free-form.
 *
 * Nav variants: when the visitor is already signed in (sessionStore
 * has an access token), the right-side CTAs swap from "Sign in /
 * Build my card" to "Go to dashboard". Doesn't load /auth/me — a
 * present token is a good-enough proxy for "this is a known visitor"
 * on a public surface.
 */
import { ReactNode } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { sessionStore } from '@/lib/api';

export function PublicShell() {
  const signedIn = !!sessionStore.getAccess();
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--ink)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PublicNav signedIn={signedIn} />
      <div style={{ flex: 1 }}>
        <Outlet />
      </div>
      <PublicFooter />
    </div>
  );
}

function PublicNav({ signedIn }: { signedIn: boolean }) {
  return (
    <nav
      className="public-shell-nav"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px 40px',
        borderBottom: '1px solid var(--line)',
        background: 'color-mix(in srgb, var(--bg) 88%, transparent)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, textDecoration: 'none', color: 'var(--ink)' }}>
        <div className="serif" style={{ fontSize: 28, fontStyle: 'italic', fontWeight: 500, letterSpacing: -0.5 }}>wydm</div>
        <div style={{ width: 6, height: 6, borderRadius: 6, background: 'var(--coral)' }} />
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {signedIn ? (
          /* "How it works" is explained on the landing page itself —
             no nav item needed. Signed-in users only see the pill CTA
             since they don't need a separate Dashboard link. */
          <Link to="/home" style={pillCta}>Go to profile</Link>
        ) : (
          <>
            <NavLinkTxt to="/auth/signin">Sign in</NavLinkTxt>
            <div style={{ width: 1, height: 22, background: 'var(--line)', margin: '0 6px' }} />
            <Link to="/auth/signup" style={pillCta}>Build my card</Link>
          </>
        )}
      </div>
    </nav>
  );
}

function NavLinkTxt({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} style={{ padding: '8px 14px', fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, textDecoration: 'none' }}>
      {children}
    </Link>
  );
}

const pillCta: React.CSSProperties = {
  padding: '10px 18px',
  background: 'var(--ink)',
  color: 'var(--bg)',
  borderRadius: 100,
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
};

function PublicFooter() {
  return (
    <footer
      style={{
        padding: '40px 40px 60px',
        borderTop: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap',
        fontSize: 12,
        color: 'var(--ink-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div className="serif" style={{ fontSize: 18, fontStyle: 'italic', fontWeight: 500 }}>wydm</div>
        <div style={{ width: 4, height: 4, borderRadius: 4, background: 'var(--coral)' }} />
        <span style={{ marginLeft: 6 }}>© 2026 · 18+ only</span>
      </div>
      <div style={{ display: 'flex', gap: 18 }}>
        <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
        <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</Link>
        <Link to="/account/delete" style={{ color: 'inherit', textDecoration: 'none' }}>Delete account</Link>
        <a href="mailto:hello@wydm.app" style={{ color: 'inherit', textDecoration: 'none' }}>Contact</a>
      </div>
    </footer>
  );
}
